import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { User as UserIcon, Briefcase } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicTechProfile() {
  const { token } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/public/tech/${token}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(setProfile)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-6">
        <div className="text-center">
          <UserIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">Profile not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="public-tech-profile">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 px-6 py-3">
        <div className="max-w-lg mx-auto flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-semibold text-slate-700">{profile.company_name}</span>
        </div>
      </div>

      {/* Profile card */}
      <div className="max-w-lg mx-auto px-6 py-10">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Header accent */}
          <div className="h-20 bg-gradient-to-r from-blue-500 to-blue-600" />
          
          {/* Avatar */}
          <div className="flex justify-center -mt-12">
            <div className="w-24 h-24 rounded-full border-4 border-white shadow-md bg-slate-100 flex items-center justify-center overflow-hidden">
              {profile.photo_url ? (
                <img src={profile.photo_url} alt={profile.name} className="w-full h-full object-cover" />
              ) : (
                <UserIcon className="w-10 h-10 text-slate-400" />
              )}
            </div>
          </div>

          {/* Info */}
          <div className="px-6 py-5 text-center">
            <h1 className="text-xl font-bold text-slate-900" data-testid="tech-name">{profile.name}</h1>
            <p className="text-sm text-blue-600 font-medium mt-0.5">{profile.company_name}</p>
            
            {profile.bio && (
              <div className="mt-5 text-left">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">About</p>
                <p className="text-sm text-slate-600 leading-relaxed" data-testid="tech-bio">{profile.bio}</p>
              </div>
            )}
          </div>
        </div>

        {/* Trust footer */}
        <p className="text-center text-xs text-slate-400 mt-6">
          Powered by Stock &amp; Load
        </p>
      </div>
    </div>
  );
}
